import {GeoTrack} from '../Models/GeoTrack';
import {Distance} from '../Domain/Distance';
import {Duration} from '../Domain/Duration';
import {Speed} from '../Domain/Speed';
import {getCategoryIndex} from './internal/getCategoryIndex';
import {mergeCategorisedSegments} from './internal/mergeCategorisedSegments';
import {GeoSegment, geoTrackToSegments} from './geoTrackToSegments';
import {smoothenGeoSegments} from './smoothenGeoSegments';

export enum IntervalType {
    Rest,
    Active,
}

/**
 * An interval is a part of the total workout similar to training plan intervals of the indoor workout.
 */
export interface GeoTrackInterval {
    type: IntervalType;
    segments: GeoSegment[];
}

function filterGlitches(geoSegments: GeoSegment[]): GeoSegment[] {
    return geoSegments.filter((segment) => {
        return segment.speed.lt(Speed.fromMetersPerSecond(10)) && segment.distance.lt(Distance.fromMeters(100));
    });
}

/**
 * Splits a GeoTrack into Intervals. Calculates and normalizes the speed between every 2 points, then splits the GeoTrack into Intervals at points where speed has changed. Will merge small Intervals with larger ones.
 * @param geoTrack The GeoTrack of which to extract Intervals.
 * @returns A list of intervals.
 */
export function geoTrackToIntervals(geoTrack: GeoTrack): GeoTrackInterval[] {
    if (geoTrack.length < 2) {
        return [];
    }

    // The max speed that can be defined as resting.
    const maxRestSpeed: Speed = Speed.fromMetersPerSecond(1);
    // The lower this number, the fewer categories will be created.
    const categoryAcceptanceTreshold: number = 0.65;
    // The amount of history to keep when considering a new section.
    const categoryHistorySize: number = 10;
    // The minimal amount of entries in the track of a section.
    const minSectionLength: number = 50;
    // The change between each interval for counting speed rarity when creating categories.
    const speedCategoryRoundingMetersPerSecond: number = 0.2;
    // The end stop speed for creating categories, which is ridiculously high.
    const speedCategoryMaxSpeed: Speed = Speed.fromMetersPerSecond(10);

    const geoSegments = smoothenGeoSegments(filterGlitches(geoTrackToSegments(geoTrack)));

    // Create histogram of speeds
    const categorisedSegments: Array<{
        segment: GeoSegment;
        categoryIndex: number;
    }> = geoSegments.map((s, index) => {
        return {segment: s, categoryIndex: -1};
    });
    const numberOfCategories = Math.ceil(speedCategoryMaxSpeed.metersPerSecond / speedCategoryRoundingMetersPerSecond);
    const occurrencesPerCategory: number[] = new Array(numberOfCategories).fill(0);
    categorisedSegments.forEach((s) => {
        occurrencesPerCategory[Math.round(s.segment.speed.metersPerSecond / speedCategoryRoundingMetersPerSecond)] += 1;
    });
    const minAcceptanceListCount: number =
        (occurrencesPerCategory.reduce((sum, count) => sum + count) / occurrencesPerCategory.length) *
        categoryAcceptanceTreshold;

    // Create speed-based categories.
    const categories: Array<{index: number; rangeMin: number; rangeMax: number}> = [
        {index: 0, rangeMin: 0, rangeMax: maxRestSpeed.metersPerSecond},
    ];
    let counterIndex: number = maxRestSpeed.metersPerSecond / speedCategoryRoundingMetersPerSecond;
    while (counterIndex < occurrencesPerCategory.length) {
        if (occurrencesPerCategory[counterIndex] > minAcceptanceListCount) {
            const categoryStart: number = counterIndex;
            while (
                counterIndex < occurrencesPerCategory.length &&
                occurrencesPerCategory[counterIndex] > minAcceptanceListCount
            ) {
                counterIndex += 1;
            }
            categories.push({
                index: categories.length,
                rangeMin: categoryStart * speedCategoryRoundingMetersPerSecond,
                rangeMax: (counterIndex + 1) * speedCategoryRoundingMetersPerSecond,
            });
        }
        counterIndex += 1;
    }

    // Add a speedcategory to each entry.
    categorisedSegments.forEach((entry) => {
        entry.categoryIndex = getCategoryIndex(categories, entry.segment.speed);
    });

    // Split the track where the surroundings average category changes.
    let prevHistAvg: number = 0;
    let hist: number[] = new Array(categoryHistorySize).fill(0);
    for (const entry of categorisedSegments) {
        // Alter history.
        const histMostCommon: number = hist.sort((a, b) =>
            hist.reduce((sum, category) => sum + (category === a ? 1 : 0)) >
            hist.reduce((sum, category) => sum + (category === b ? 1 : 0))
                ? -1
                : 1,
        )[0];
        hist = hist.slice(1).concat(entry.categoryIndex);

        // Set histories average to entry.
        entry.categoryIndex = histMostCommon;
        prevHistAvg = histMostCommon;
    }

    // Split entries into intervals.
    let sections: Array<{
        avgSpeed: Speed;
        categoryIndex: number;
        segments: GeoSegment[];
    }> = [];
    let startIndex: number = 0;
    while (startIndex < categorisedSegments.length) {
        let endIndex: number = startIndex;
        while (
            endIndex < categorisedSegments.length &&
            categorisedSegments[startIndex].categoryIndex === categorisedSegments[endIndex].categoryIndex
        ) {
            endIndex++;
        }

        const segments = categorisedSegments.slice(startIndex, endIndex).map((e) => {
            return e.segment;
        });
        sections.push({
            segments: segments,
            categoryIndex: categorisedSegments[startIndex].categoryIndex,
            avgSpeed: Speed.fromDistanceAndDuration(
                Distance.fromMillimeters(
                    segments
                        .map((s) => s.distance.millimeters)
                        .flat()
                        .reduce((sum, distance) => sum + distance),
                ),
                Duration.fromMilliseconds(
                    segments
                        .map((s) => s.duration.milliseconds)
                        .flat()
                        .reduce((sum, duration) => sum + duration),
                ),
            ),
        });
        startIndex = endIndex;
    }

    // Remove small sections from smallest to large.
    while (sections.filter((s) => s.segments.length < minSectionLength).length > 0 && sections.length > 1) {
        const smallestIndex: number = sections
            .map((data, index) => {
                return {data: data, index: index};
            })
            .filter((d) => d.data.segments.length < minSectionLength)
            .sort((a, b) => (a.data.segments.length < b.data.segments.length ? -1 : 1))[0].index;

        // Find the best neighbor to merge with.
        let mergeIndexOffset: number = 0;
        if (smallestIndex === 0) {
            mergeIndexOffset = 1;
        } else if (smallestIndex === sections.length - 1) {
            mergeIndexOffset = -1;
        } else {
            const speedDifferencePrevious = Math.abs(
                sections[smallestIndex].avgSpeed.metersPerSecond - sections[smallestIndex - 1].avgSpeed.metersPerSecond,
            );

            const speedDifferenceNext = Math.abs(
                sections[smallestIndex].avgSpeed.metersPerSecond - sections[smallestIndex + 1].avgSpeed.metersPerSecond,
            );

            mergeIndexOffset = speedDifferencePrevious < speedDifferenceNext ? -1 : 1;
        }
        sections = mergeCategorisedSegments(
            Math.min(smallestIndex, smallestIndex + mergeIndexOffset),
            Math.max(smallestIndex, smallestIndex + mergeIndexOffset),
            sections,
            categories,
        );
    }

    // Merge sections with the same speedCategory.
    let sectionIndex: number = 0;
    for (let i = 0; i < 10; i += 1) {
        while (sectionIndex < sections.length - 1) {
            if (sections[sectionIndex].categoryIndex === sections[sectionIndex + 1].categoryIndex) {
                sections = mergeCategorisedSegments(sectionIndex, sectionIndex + 1, sections, categories);
            } else {
                sectionIndex += 1;
            }
        }
    }

    return sections.map((section) => {
        return {
            type: section.categoryIndex === 0 ? IntervalType.Rest : IntervalType.Active,
            segments: section.segments.map((sectionEntry) => sectionEntry),
        };
    });
}
